import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Paper, Tooltip } from '@material-ui/core';
import RightArrow from '@material-ui/icons/ChevronRightRounded';
import NoteIcon from '@material-ui/icons/Feedback';
import CornerArrow from '@material-ui/icons/TransitEnterexit';
import RemoveIcon from '@material-ui/icons/CloseRounded';
import AddIcon from '@material-ui/icons/Add';

import { GlobalContext } from '../../store';
import { IEventEnrollment } from '../../store/admin/eventEnrollments/store';
import { IEventDetailedRound, IRoundGame } from '../../store/admin/eventRounds/store';

import { toHandicap } from '../../contrib/Utils/toHandicap';
import { NewPlayerCardStyled } from './PlayerCard.Styles';
import { AddIconContainerStyled } from './AddIconContainer.Styles';
import { RemoveModal } from '../TeamCard/RemoveTeamModal';
import { useBoolean } from '../../scenes/common/Hooks/useBoolean.hook';
import { useAdminRound } from '../../contrib/Hooks/useAdminRound.hook';
import { useRouteInfoState } from '../RouteInfo/Context/RouteInfo.context';
import { IncompleteEnrollmentAlert } from './IncompleteEnrollmentAlert.Styles';
import { userPic } from '../../assets/images';
import { SwapIconStyled } from './SwapIcon.Styles';

type TOnAddClick = (itemId: number) => () => void;

interface IProps {
  item: IEventEnrollment;
  context?: {
    hideNote?: boolean;
    isDragging?: boolean;
    onAddClick?: TOnAddClick;
    onArrowClick?: any;
    onRemovePlayer?: (enrollmentId: number) => () => void;
    onSwapPlayer?: (enrollmentId: number) => () => void;
    playerIsSelected?: boolean;
    round: IEventDetailedRound;
    swappingInProcess?: boolean;
  };
}

const PlayerCard: FC<IProps> = props => {
  const {
    item,
    item: {
      id: enrollmentId,
      user: {
        profile: { image },
        firstName,
        lastName
      }
    },
    context
  } = props;

  const {
    actions: {
      playerEditorModal: { openModal, selectEnrollment },
      adminEventRounds: { get: getRound }
    }
  } = useContext(GlobalContext);

  const [playerTeeId, setPlayerTeeId] = useState<number>();
  const [teeName, setTeeName] = useState<string>('TBD');
  const [gameIndex, setGameIndex] = useState<number>();
  const [gameAdjustmentHCP, setGameAdjustmentHCP] = useState<number>();
  const [gameHandicap, setGameHandicap] = useState<number>();
  const [game, setGame] = useState<IRoundGame>();

  const { roundId, clubId, eventId } = useRouteInfoState();

  const [round] = useAdminRound(roundId);

  useEffect(() => {
    if (round && round.groups) {
      const games = round.groups.flatMap(group => group.games);

      const gameFound = games.find(roundGame => roundGame.enrollment === enrollmentId);

      if (gameFound) {
        setPlayerTeeId(gameFound.tee);
        setGameIndex(gameFound.index);
        setGameAdjustmentHCP(gameFound.adjustedHandicap);
        setGameHandicap(gameFound.handicap);
        setGame(gameFound);
      }
    }
  }, [round, enrollmentId]);

  useEffect(() => {
    if (round && round.course && round.course.tees) {
      const playerTee = round.course.tees.find(tee => tee.id === playerTeeId);

      if (!!playerTee) {
        setTeeName(playerTee.name);
      }
    }
  }, [playerTeeId, round]);

  const onArrowClick = context && context.onArrowClick ? () => context.onArrowClick(item) : null;

  const onRemovePlayer = context && context.onRemovePlayer ? context.onRemovePlayer(item.id) : null;

  const onSwapPlayer = context && context.onSwapPlayer ? context.onSwapPlayer(item.id) : null;

  const hideNote = context && context.hideNote;

  const [removeModalIsOpen, , toggleRemoveModalIsOpen] = useBoolean(false);

  const onCornerArrowClick = useCallback(async () => {
    await openModal();
    await selectEnrollment(item);
  }, [openModal, selectEnrollment, item]);

  const [enrollmentIsIncomplete, setEnrollmentIsIncomplete] = useState(false);

  useEffect(() => {
    if (game) {
      setEnrollmentIsIncomplete(!game.tee || !item.flight);
    }
  }, [game, item]);

  const onAddPlayer = async () => {
    if (context && context.onAddClick && clubId && eventId && roundId) {
      await context.onAddClick(item.id)();

      // TODO: remove this. This is a hack to get the full round info of the games once a player is
      //       added to the teesheet. That info should come from the backend
      await getRound(Number(clubId), Number(eventId), Number(roundId), true);
    }
  };

  return (
    <NewPlayerCardStyled className="player-card" isDragging={!!context && !!context.isDragging}>
      {enrollmentIsIncomplete && (
        <IncompleteEnrollmentAlert onClick={onCornerArrowClick}>
          <p>
            Player’s index, tees, and/or flight assigment(s) must be updated before starting the
            round.
          </p>

          <p>
            <CornerArrow
              onClick={onCornerArrowClick}
              className="top-left-corner-icon corner-arrow"
              color="primary"
              fontSize="small"
            />
            Click here to edit
          </p>
        </IncompleteEnrollmentAlert>
      )}

      <Paper elevation={1} className="card-paper">
        <div className="img">
          <img src={image || userPic} alt={`${firstName} ${lastName} avatar`} />
        </div>

        <div className="player-card-info">
          <div className="name">{`${firstName} ${lastName}`}</div>

          <div className="index-tee">
            <div className="index">
              <span className="label">IND</span>

              <span className="value">{toHandicap(gameIndex)}</span>
            </div>

            <div className="tee">{`${teeName} TEE`}</div>
          </div>

          <div className="index-tee">
            <div className="index">
              <span className="label">HCP</span>

              <span className="value">{toHandicap(gameHandicap)}</span>
            </div>
          </div>

          <div className="index-tee">
            <div className="index">
              <span className="label">ADJ</span>

              <span className="value">{toHandicap(gameAdjustmentHCP)}</span>
            </div>
          </div>
        </div>

        {game && (
          <CornerArrow
            onClick={onCornerArrowClick}
            className="top-left-corner-icon corner-arrow"
            color="primary"
            fontSize="small"
          />
        )}

        {!hideNote && item.playerNote && (
          <Tooltip
            className="top-right-corner-icon comment-icon"
            title={item.playerNote}
            aria-label={item.playerNote}
          >
            <NoteIcon color="primary" />
          </Tooltip>
        )}

        {onSwapPlayer && (
          <Tooltip title="Swap Players" placement="top">
            <SwapIconStyled
              playerIsSelected={context && context.playerIsSelected}
              swappingInProcess={context && context.swappingInProcess}
              className="bottom-right-corner-icon swap-player-icon"
              onClick={onSwapPlayer}
              color="primary"
            />
          </Tooltip>
        )}

        {onRemovePlayer && (
          <>
            <RemoveIcon
              className="top-right-corner-icon remove-player-icon"
              onClick={toggleRemoveModalIsOpen}
              color="primary"
            />
            <RemoveModal
              modalIsOpen={removeModalIsOpen}
              toggleModalIsOpen={toggleRemoveModalIsOpen}
              onRemove={onRemovePlayer}
              resourceName="Player"
            />
          </>
        )}

        {onArrowClick && (
          <RightArrow className="right-arrow" color="primary" onClick={onArrowClick} />
        )}

        {context && context.onAddClick && (
          <AddIconContainerStyled onClick={onAddPlayer}>
            <AddIcon />
          </AddIconContainerStyled>
        )}
      </Paper>
    </NewPlayerCardStyled>
  );
};

export default PlayerCard;
